import { useEffect, useState, useRef, useCallback } from 'react';
import { api } from '../api';
import { formatDateTime } from '../utils';
import { useTranslation } from 'react-i18next';
import HelpHint from '../components/HelpHint';

function formatTs(s) {
  if (!s) return '';
  const iso = s.replace(' ', 'T') + (s.includes('T') ? '' : 'Z');
  return formatDateTime(iso);
}

export default function Czat() {
  const { t } = useTranslation();
  const [msgs, setMsgs]         = useState([]);
  const [convStatus, setStatus] = useState('aktywna');
  const [text, setText]         = useState('');
  const [loading, setLoading]   = useState(true);
  const [sending, setSending]   = useState(false);
  const [resuming, setResuming] = useState(false);
  const [error, setError]       = useState(null);
  const bottomRef = useRef(null);

  const scrollBottom = () => setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 60);

  const load = useCallback(async (initial = false) => {
    try {
      const data = await api.getCzat();
      setMsgs(prev => {
        // jeśli nic się nie zmieniło — pomiń re-render (uniknij scrolla podczas pollingu)
        if (prev.length === data.msgs.length && prev[prev.length - 1]?.id === data.msgs[data.msgs.length - 1]?.id) {
          return prev;
        }
        return data.msgs;
      });
      setStatus(data.conv_status || 'aktywna');
      if (initial) scrollBottom();
      setError(null);
    } catch (e) {
      console.error('[czat] load error:', e);
      setError('Nie udało się załadować wiadomości: ' + (e.message || 'błąd połączenia'));
    } finally {
      if (initial) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(true);
    const id = setInterval(() => load(false), 20000);
    return () => clearInterval(id);
  }, [load]);

  // Auto-scroll na dół po pojawieniu się nowych wiadomości
  useEffect(() => {
    if (msgs.length > 0) scrollBottom();
  }, [msgs.length]);

  const send = async () => {
    const t = text.trim();
    if (!t || sending || convStatus === 'zakonczona') return;
    setSending(true);
    setError(null);
    try {
      const msg = await api.sendCzat(t);
      setMsgs(prev => [...prev, msg]);
      setText('');
      scrollBottom();
    } catch (e) {
      setError('Nie udało się wysłać wiadomości: ' + (e.message || 'błąd połączenia'));
    } finally {
      setSending(false);
    }
  };

  const resume = async () => {
    if (resuming) return;
    setResuming(true);
    setError(null);
    try {
      await api.wznowCzat();
      setStatus('aktywna');
    } catch (e) {
      setError('Nie udało się wznowić konwersacji: ' + (e.message || 'błąd połączenia'));
    } finally {
      setResuming(false);
    }
  };

  const handleKey = e => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const isClosed = convStatus === 'zakonczona';

  // Liczba zakończonych konwersacji w historii (separator system=1) — info do nagłówka
  const ended = msgs.filter(m => m.system === 1 || m.system === true).length;

  return (
    <div style={{ maxWidth: '680px', width: '100%' }}>
      {/* Nagłówek */}
      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ fontSize: '20px', fontWeight: 700, color: 'var(--ink)', margin: '0 0 4px', display: 'inline-flex', alignItems: 'center', gap: '10px' }}>
          Wsparcie ScanPilots
          <HelpHint text={t('help.supportChat')} label="Wsparcie ScanPilots" />
        </h2>
        <p style={{ fontSize: '12px', color: 'var(--ink3)', margin: 0 }}>
          Masz pytanie, sugestię lub problem? Napisz do nas.
          {ended > 0 && (
            <span style={{ marginLeft: '6px' }}>
              · Historia: {ended} {ended === 1 ? 'zakończona konwersacja' : ended < 5 ? 'zakończone konwersacje' : 'zakończonych konwersacji'}
            </span>
          )}
        </p>
      </div>

      {/* Baner 24h */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '10px',
        background: 'var(--bg2)', border: '1px solid var(--border)', borderLeft: '3px solid var(--acc)',
        borderRadius: '8px', padding: '10px 14px', marginBottom: '16px',
      }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--acc)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
        </svg>
        <p style={{ fontSize: '12px', color: 'var(--ink2)', margin: 0, lineHeight: 1.5 }}>
          Odpowiadamy na wszystkie wiadomości w ciągu <strong style={{ color: 'var(--ink)' }}>24 godzin</strong> w dni robocze.
        </p>
      </div>

      {/* Obszar czatu */}
      <div style={{
        height: 'min(60vh, 480px)', minHeight: '280px', overflowY: 'auto',
        background: 'var(--bg2)', border: '1px solid var(--border)',
        borderRadius: '10px', padding: '16px', marginBottom: '10px',
        display: 'flex', flexDirection: 'column',
      }}>
        {loading && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <p style={{ fontSize: '12px', color: 'var(--ink3)' }}>Ładowanie…</p>
          </div>
        )}

        {!loading && msgs.length === 0 && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--ink3)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
            <p style={{ fontSize: '12px', color: 'var(--ink3)', textAlign: 'center', margin: 0 }}>
              Brak wiadomości — napisz do nas, chętnie pomożemy!
            </p>
          </div>
        )}

        {!loading && msgs.map(msg => {
          // Wiadomość systemowa = baner zakończenia (separator + treść pożegnalna)
          if (msg.system === 1 || msg.system === true) {
            return (
              <div key={msg.id} style={{ margin: '14px 0 18px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                  <div style={{ flex: 1, height: '1px', background: 'var(--border)' }} />
                  <span style={{
                    fontSize: '9px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase',
                    color: 'var(--ink3)', background: 'var(--bg3)', padding: '3px 10px', borderRadius: '10px',
                    border: '1px solid var(--border)',
                  }}>
                    Konwersacja zakończona · {formatTs(msg.utworzono)}
                  </span>
                  <div style={{ flex: 1, height: '1px', background: 'var(--border)' }} />
                </div>
                <div style={{
                  background: 'var(--bg3)', border: '1px solid var(--border)',
                  borderLeft: '3px solid var(--acc)', borderRadius: '8px',
                  padding: '12px 14px',
                }}>
                  <p style={{
                    fontSize: '9px', fontWeight: 700, color: 'var(--acc)',
                    margin: '0 0 4px', letterSpacing: '0.05em',
                  }}>SCANPILOTS</p>
                  <p style={{
                    fontSize: '12px', lineHeight: 1.55, margin: 0,
                    color: 'var(--ink2)', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                  }}>
                    {msg.tresc}
                  </p>
                </div>
              </div>
            );
          }

          // Zwykła wiadomość
          return (
            <div key={msg.id} style={{
              display: 'flex',
              justifyContent: msg.nadawca === 'user' ? 'flex-end' : 'flex-start',
              marginBottom: '10px',
            }}>
              <div style={{
                maxWidth: '75%',
                background: msg.nadawca === 'user' ? 'var(--acc)' : 'var(--bg3)',
                border: msg.nadawca === 'user' ? 'none' : '1px solid var(--border)',
                borderRadius: msg.nadawca === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                padding: '9px 13px',
              }}>
                {msg.nadawca === 'admin' && (
                  <p style={{ fontSize: '9px', fontWeight: 700, color: 'var(--acc)', marginBottom: '4px', letterSpacing: '0.05em' }}>
                    SCANPILOTS
                  </p>
                )}
                <p style={{
                  fontSize: '13px', lineHeight: 1.55, margin: 0,
                  color: msg.nadawca === 'user' ? '#fff' : 'var(--ink)',
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                }}>
                  {msg.tresc}
                </p>
                <p style={{
                  fontSize: '9px', margin: '5px 0 0',
                  color: msg.nadawca === 'user' ? 'rgba(255,255,255,0.55)' : 'var(--ink3)',
                  textAlign: 'right',
                }}>
                  {formatTs(msg.utworzono)}
                </p>
              </div>
            </div>
          );
        })}

        <div ref={bottomRef} />
      </div>

      {/* Błąd */}
      {error && (
        <p style={{ fontSize: '11px', color: 'var(--err2)', marginBottom: '6px' }}>{error}</p>
      )}

      {/* Stopka — input lub panel wznawiania */}
      {isClosed ? (
        <div style={{
          background: 'var(--bg2)', border: '1px solid var(--border)',
          borderLeft: '3px solid var(--acc)', borderRadius: '8px',
          padding: '14px 16px',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: '12px', flexWrap: 'wrap',
        }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <p style={{ fontSize: '12px', fontWeight: 700, color: 'var(--ink)', margin: '0 0 2px' }}>
              Konwersacja zakończona
            </p>
            <p style={{ fontSize: '11px', color: 'var(--ink3)', margin: 0, lineHeight: 1.45 }}>
              Aby kontynuować rozmowę lub zadać nowe pytanie, wznów konwersację.
            </p>
          </div>
          <button
            onClick={resume}
            disabled={resuming}
            style={{
              background: 'var(--acc)',
              border: 'none',
              borderRadius: '8px',
              color: '#fff',
              fontSize: '12px',
              fontWeight: 700,
              padding: '10px 18px',
              cursor: resuming ? 'wait' : 'pointer',
              whiteSpace: 'nowrap',
              opacity: resuming ? 0.7 : 1,
            }}
          >
            {resuming ? 'Wznawiam…' : 'Wznów konwersację'}
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Napisz wiadomość… (Enter = wyślij, Shift+Enter = nowa linia)"
            rows={3}
            style={{
              flex: 1,
              background: 'var(--bg2)',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              color: 'var(--ink)',
              fontSize: '13px',
              padding: '10px 12px',
              resize: 'none',
              outline: 'none',
              lineHeight: 1.5,
              fontFamily: 'inherit',
            }}
          />
          <button
            onClick={send}
            disabled={!text.trim() || sending}
            style={{
              padding: '10px 18px',
              borderRadius: '8px',
              border: 'none',
              background: text.trim() && !sending ? 'var(--acc)' : 'var(--bg4)',
              color: text.trim() && !sending ? '#fff' : 'var(--ink3)',
              fontSize: '12px',
              fontWeight: 700,
              cursor: text.trim() && !sending ? 'pointer' : 'not-allowed',
              transition: 'all 0.15s',
              whiteSpace: 'nowrap',
              height: '44px',
            }}
          >
            {sending ? '…' : 'Wyślij'}
          </button>
        </div>
      )}
    </div>
  );
}
